import {
  Button,
  Callout,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
} from '@dev-spendesk/grapes';
import type { TFunction } from 'i18next';
import React, { useReducer, useState } from 'react';
import { Trans } from 'react-i18next';

import { type Token, TokenInput } from 'common/components/TokenInput';
import { useTranslation } from 'common/hooks/useTranslation';
import { validateLoginAuthenticationOperation } from 'src/auth/modules/StrongCustomerAuthentication/api/validateScaProcedureWithTotp';

type FlowProps =
  | { flow: 'auth'; scaToken: string; operationId?: never }
  | { flow: 'standard'; scaToken?: never; operationId?: string };

type Props = {
  onBack: () => void;
  onRecoveryTokenValidated: () => Promise<void> | void;
} & FlowProps;

type TokenState = {
  token: Token;
  isTokenFilled: boolean;
};

const TOTP_TOKEN_LENGTH = 6;
const emptyToken: Token = new Array(TOTP_TOKEN_LENGTH).fill('');

function tokenReducer(tokenState: TokenState, action: Token): TokenState {
  const newToken = [...action, ...emptyToken].slice(0, TOTP_TOKEN_LENGTH);
  return {
    ...tokenState,
    token: newToken,
    isTokenFilled: newToken.every((char) => char !== ''),
  };
}

export const RecoveryModal = ({
  flow,
  onBack,
  onRecoveryTokenValidated,
  scaToken,
}: Props) => {
  const { t } = useTranslation('auth');

  const [{ token, isTokenFilled }, setToken] = useReducer(tokenReducer, {
    token: emptyToken,
    isTokenFilled: false,
  });

  const [verifyError, setVerifyError] = useState<
    'token-invalid' | 'token-expired' | undefined
  >(undefined);

  const handleValidateToken = async () => {
    const combinedToken = token.join('');
    if (flow === 'auth' && scaToken) {
      const result = await validateLoginAuthenticationOperation(
        combinedToken,
        scaToken,
        false,
      );
      if (result.outcome === 'failure') {
        setVerifyError(result.errorCode);
        return;
      }

      if (result.outcome === 'success') {
        await onRecoveryTokenValidated();
      }
    }
  };

  return (
    <ModalContent>
      <ModalHeaderWithIcon
        title={t(
          'sca.verifyAuthenticationOperationModal.methods.recovery.title',
        )}
        iconName="lock-close"
        iconVariant="neutral"
        titleId="select-auth-factor-modal-title"
      />
      <ModalBody>
        <p>
          <Trans
            i18nKey="sca.verifyAuthenticationOperationModal.methods.recovery.description"
            t={t as TFunction<'auth'>}
          />
        </p>
        <div className="flex w-full flex-col items-center justify-center px-40 py-32">
          <TokenInput
            token={token}
            tokenLength={TOTP_TOKEN_LENGTH}
            onTokenChange={setToken}
          />
        </div>
        {verifyError && (
          <Callout
            title={t(
              'sca.verifyAuthenticationOperationModal.errors.tokenInvalid',
            )}
            variant="alert"
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onBack}
        />
        <Button
          variant="primaryBrand"
          text={t('misc.continue')}
          isDisabled={!isTokenFilled}
          onClick={handleValidateToken}
        />
      </ModalFooter>
    </ModalContent>
  );
};
